import { format as d3Format } from 'd3-format';

/**
 * Replaces template variables in a string with actual values.
 * Template variables are in the format {variableName} or {variableName:format}.
 * 
 * Supported formats (using d3-format):
 * - {value:.1f} - Format as float with 1 decimal place
 * - {value:.2f} - Format as float with 2 decimal places
 * - {value:d} - Format as integer (no decimals)
 * - {value:,.2f} - Format with thousands separator and 2 decimals
 * - {value:.0%} - Format as percentage
 * - See https://github.com/d3/d3-format for all format options
 * 
 * @param template - The template string containing variables like {name}, {value}, etc.
 * @param values - An object mapping variable names to their values
 * @returns The template string with variables replaced by their values
 * 
 * @example
 * replaceTemplateValues('**{name}**\nValue: {value:.1f}%', { name: 'Sales', value: 46.25 })
 * // Returns: '**Sales**\nValue: 46.3%'
 */
export function replaceTemplateValues(
  template: string,
  values: Record<string, string | number | undefined>
): string {
  return template.replace(/\{(\w+)(?::([^}]+))?\}/g, (match, key, formatSpec) => {
    const value = values[key];

    if (value === undefined) {
      return match;
    }

    // If no format specified, return as-is
    if (!formatSpec) {
      return String(value);
    }

    // Try to format as number using d3-format
    const numValue = typeof value === 'number' ? value : parseFloat(String(value));

    if (isNaN(numValue)) {
      return String(value);
    }

    try {
      return d3Format(formatSpec)(numValue);
    } catch (error) {
      // If format is invalid, return as-is
      console.warn(`Invalid format specifier "${formatSpec}" for value ${numValue}`);
      return String(value);
    }
  });
}

/**
 * Type definition for template variable values that can be used in chart tooltips
 */
export type TooltipTemplateValues = Record<string, string | number | undefined>;
